import { Button, Flex, Select, SelectProps, Text } from "@mantine/core";
import { EventHandler, forwardRef, useState } from "react";

import { useModal } from "@/features/modal/ModalStackManager";

import styled from "@emotion/styled";

import { mutatePurchaseOrderCategories } from "@/api/purchaseOrderCategories/usePurchaseOrderCategoriesQuery";
import { users } from "@/api/users/useUsersQuery";
import { PurchaseOrderCategoryCreateForm } from "@/features/purchaseOrderCategory/form/CreateForm";
import { handleErrorResponse } from "@/utils/errorMessage";
import { translateWithPartnerType } from "@/utils/partnerType";
import { ScmApiPurchaseOrderCategoriesPostRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { IconX } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import customAlert from "../../alert/Alert";
import {
  PurchaseOrderCategoryOption,
  usePurchaseOrderCategoriesAutoComplete,
  usePurchaseOrderCategoriesCode,
} from "./auto-useGetPurchaseOrderCategory";

interface PurchaseOrderCategoryProps extends React.ComponentPropsWithoutRef<"div"> {
  label: string;
  value: string;
}

export interface PurchaseOrderCategoryAutoCompleteProps
  extends Partial<SelectProps>,
    Partial<React.RefAttributes<HTMLInputElement>> {
  value?: string | null;
  onChange?: (PurchaseOrderCategoryName: string | null) => void;
  maxDropdownHeight?: number;
  width?: string;
}

// 입력값: value (ItemCategory 모델의 code)
// 출력값: onChange (ItemCategory 모델의 code)
export const PurchaseOrderCategoryAutoComplete = (
  params: PurchaseOrderCategoryAutoCompleteProps,
) => {
  const {
    value: PurchaseOrderCategoryName,
    onChange,
    maxDropdownHeight,
    width,
    ...etcParams
  } = params;
  const [focused, setFocused] = useState<boolean>(true);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const { data: options } = usePurchaseOrderCategoriesAutoComplete(focused, searchKeyword);

  const { data: initialOptions } = usePurchaseOrderCategoriesCode(
    !!PurchaseOrderCategoryName,
    PurchaseOrderCategoryName ?? null,
  );

  let selectedPurchaseOrderCategory = initialOptions?.find(
    (PurchaseOrderCategory) => PurchaseOrderCategory.value === PurchaseOrderCategoryName,
  );
  const onChangeHandler = (e: string | null) => {
    selectedPurchaseOrderCategory = options?.find(
      (PurchaseOrderCategory) => PurchaseOrderCategory.value === e,
    );
    onChange && onChange(e);
  };

  const SelectPurchaseOrderCategory = forwardRef<HTMLDivElement, PurchaseOrderCategoryProps>(
    ({ label: name, value: code, ...others }, ref) => (
      <div ref={ref} {...others}>
        <Flex direction="row" justify="space-between" align="center">
          <Flex align={"center"}>
            <Text>{name}</Text>
            <Text fz="xs">(수주유형명: {code})</Text>
          </Flex>
        </Flex>
      </div>
    ),
  );

  return (
    <PurchaseOrderCategorySelect
      withinPortal
      onDropdownOpen={() => setFocused(true)}
      onDropdownClose={() => setFocused(false)}
      clearable
      value={PurchaseOrderCategoryName}
      itemComponent={SelectPurchaseOrderCategory}
      searchValue={searchKeyword}
      data={[...(options ?? []), ...(initialOptions ?? [])].reduce(
        (unique: PurchaseOrderCategoryProps[], option: PurchaseOrderCategoryProps) => {
          return unique.some((u) => u.value === option.value) ? unique : [...unique, option];
        },
        [],
      )}
      searchable
      maxDropdownHeight={maxDropdownHeight ?? 150}
      onChange={onChangeHandler}
      onSearchChange={setSearchKeyword}
      rightSection={PurchaseOrderCategoryInfo({
        PurchaseOrderCategory: selectedPurchaseOrderCategory as PurchaseOrderCategoryProps,
        onChange: onChangeHandler,
      })}
      filter={(value, item) =>
        item?.label?.toLowerCase().includes(value.toLowerCase().trim()) ||
        item?.value?.toLowerCase().includes(value.toLowerCase().trim())
      }
      nothingFound={AddNewPurchaseOrderCategory({
        PurchaseOrderCategory: searchKeyword,
        onChange: onChangeHandler,
      })}
      {...etcParams}
    />
  );
};

const PurchaseOrderCategoryInfo = (params: {
  PurchaseOrderCategory?: PurchaseOrderCategoryOption;
  onChange: (PurchaseOrderCategoryName: string | null) => void;
}) => {
  const { PurchaseOrderCategory, onChange } = params;

  const clearHandler: React.MouseEventHandler<SVGSVGElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(null);
  };

  return PurchaseOrderCategory?.value ? (
    <PurchaseOrderCategoryInfoLabel>
      <IconX size="1rem" onClick={clearHandler} />
    </PurchaseOrderCategoryInfoLabel>
  ) : null;
};

const AddNewPurchaseOrderCategory = (params: {
  PurchaseOrderCategory: string;
  onChange: (PurchaseOrderCategoryName: string) => void;
}) => {
  const { PurchaseOrderCategory, onChange } = params;
  const { openModal, closeModal } = useModal();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { data: userInfo } = useQuery(users.who({}));
  const partnerType = userInfo?.data?.partner?.type;

  const { mutate: postMutate } = useMutation(
    (params: ScmApiPurchaseOrderCategoriesPostRequest) =>
      mutatePurchaseOrderCategories
        .create(params)
        .mutationFn(params as ScmApiPurchaseOrderCategoriesPostRequest | any),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(["purchaseOrderCategories"]);
        closeModal(res);
        customAlert(
          t("message.createSuccess", {
            item: translateWithPartnerType(t, "purchaseOrder.categoryName", partnerType),
          }),
          t("common.createSuccess"),
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({
            error,
            errorContext: translateWithPartnerType(t, "purchaseOrder.categoryName", partnerType),
          }),
          t("common.createFail", {
            item: translateWithPartnerType(t, "purchaseOrder.categoryName", partnerType),
          }),
          "red",
        );
      },
    },
  );

  const onCloseHandler: EventHandler<any> = (values) => {
    postMutate({
      purchaseOrderCategoriesGetRequest: {
        name: values.name!,
      },
    });
  };
  const addNewModalHandler: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    openModal(
      <PurchaseOrderCategoryCreateForm
        name={PurchaseOrderCategory}
        autoCompleteOnSubmit={onCloseHandler}
      />,
      null,
      t("common.newCreate", {
        item: translateWithPartnerType(t, "purchaseOrder.categoryName", partnerType),
      }),
      true,
    ).then((result) => {
      onChange(result.name);
    });
  };

  return (
    <Button variant="subtle" onClick={addNewModalHandler}>
      {t("common.newCreate", {
        item: `${translateWithPartnerType(t, "purchaseOrder.categoryName", partnerType)} ${PurchaseOrderCategory}`,
      })}
    </Button>
  );
};

const PurchaseOrderCategoryInfoLabel = styled.div`
  font-size: 12px;
  color: #666666;
  // padding-right: 8px;
  padding-left: 0.4rem;
  display: flex;
`;

const PurchaseOrderCategorySelect = styled(Select)<{ value: string | null | undefined }>`
  .mantine-Select-input {
    font-size: 0.8rem;
    padding-bottom: ${(props) => (props.value ? "0.8rem !important" : "0.4rem !important")};

    &.item-dirty {
      color: cadetblue !important;
      border-color: cadetblue;
    }
  }
  .mantine-Select-error {
    font-size: 11px;
    color: red;
  }
`;
